import React, { useState, useEffect } from 'react';
import Image from 'components/AppImage';
import Button from 'components/ui/Button';
import Icon from 'components/AppIcon';
import VideoModal from 'components/ui/VideoModal';

const HeroCarousel = ({ onBookNowClick }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);

  const heroSlides = [
    {
      id: 1,
      image: "https://cdn.pixabay.com/photo/2013/08/16/16/54/kali-river-173169_1280.jpg",
      title: "Exciting Kali River Rafting",
      subtitle: "Navigate through exciting rapids on the Kali River with our expert guides",
      description: "Immerse yourself in luxury accommodations surrounded by pristine forests and adventure activities",
      marketing: "Book now just at ₹1,499/-"
    },
    {
      id: 2,
      image: "https://images.pixabay.com/photo/2016/11/29/05/45/adventure-1867297_1280.jpg",
      title: "Adventure Awaits You",
      subtitle: "White-water rafting, jungle safaris & more",
      description: "Create unforgettable memories with thrilling activities in Karnataka\'s adventure capital",
      marketing: "Limited time offer - Save up to 30%"
    },
    {
      id: 3,
      image: "https://images.unsplash.com/photo-1571896349842-33c89424de2d",
      title: "Luxury Meets Wilderness",
      subtitle: "Premium resorts in nature\'s embrace",
      description: "Discover our exclusive accommodations from tree houses to riverside cottages",
      marketing: "Starting from ₹1,499/- per night"
    },
     {
      id: 4,
      image: "https://images.unsplash.com/photo-1571896349842-33c89424de2d",
      title: "Escape to Nature\'s Paradise",
      subtitle: "Premium resorts in nature\'s embrace",
      description: "Discover our exclusive accommodations from tree houses to riverside cottages",
      marketing: "Starting from ₹1,499/- per night"
    }
  ];

  // Demo video URL - using a nature/resort video from a free source
  const demoVideoUrl = "https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ForBiggerBlazes.mp4";

  useEffect(() => {
    if (!isAutoPlaying) return;

    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroSlides?.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [isAutoPlaying, heroSlides?.length]);

  const goToSlide = (index) => {
    setCurrentSlide(index);
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 10000);
  };

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % heroSlides?.length);
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 10000);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + heroSlides?.length) % heroSlides?.length);
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 10000);
  };

  const handleWatchVideo = () => {
    setIsVideoModalOpen(true);
  };

  const closeVideoModal = () => {
    setIsVideoModalOpen(false);
  };

  return (
    <>
      <div className="relative h-screen overflow-hidden">
        {/* Carousel Images */}
        <div className="relative w-full h-full">
          {heroSlides?.map((slide, index) => (
            <div
              key={slide?.id}
              className={`absolute inset-0 transition-opacity duration-1000 ${
                index === currentSlide ? 'opacity-100' : 'opacity-0'
              }`}
            >
              <Image
                src={slide?.image}
                alt={slide?.title}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-black/40"></div>
            </div>
          ))}
        </div>

        {/* Content Overlay */}
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center text-white px-6 max-w-4xl">
            {/* Marketing Banner */}
            <div className="inline-block bg-accent/90 text-accent-foreground px-4 py-2 rounded-full text-sm font-heading font-semibold mb-4 animate-pulse">
              🎉 {heroSlides?.[currentSlide]?.marketing}
            </div>
            
            <h1 className="text-4xl md:text-6xl font-heading font-bold mb-4 text-balance">
              {heroSlides?.[currentSlide]?.title}
            </h1>
            <p className="text-xl md:text-2xl font-medium mb-2 text-accent">
              {heroSlides?.[currentSlide]?.subtitle}
            </p>
            <p className="text-lg md:text-xl mb-8 opacity-90 max-w-2xl mx-auto font-body">
              {heroSlides?.[currentSlide]?.description}
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                variant="default"
                size="lg"
                iconName="Calendar"
                iconPosition="left"
                onClick={onBookNowClick}
                className="bg-primary hover:bg-primary/90 hover:scale-105 text-primary-foreground px-8 py-3 transition-organic hover-lift"
              >
                Book Your Stay
              </Button>
              <Button
                variant="outline"
                size="lg"
                iconName="Play"
                iconPosition="left"
                onClick={handleWatchVideo}
                className="border-white text-white hover:bg-white hover:text-primary hover:scale-105 px-8 py-3 transition-organic"
              >
                Watch Video
              </Button>
            </div>
          </div>
        </div>

        {/* Navigation Arrows */}
        <button
          onClick={prevSlide}
          className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-full flex items-center justify-center text-white transition-organic"
          aria-label="Previous slide"
        >
          <Icon name="ChevronLeft" size={24} />
        </button>
        <button
          onClick={nextSlide}
          className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-full flex items-center justify-center text-white transition-organic"
          aria-label="Next slide"
        >
          <Icon name="ChevronRight" size={24} />
        </button>

        {/* Slide Indicators */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex space-x-2">
          {heroSlides?.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`w-3 h-3 rounded-full transition-organic ${
                index === currentSlide ? 'bg-white' : 'bg-white/50'
              }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>

        {/* Auto-play indicator */}
        <div className="absolute top-4 right-4">
          <button
            onClick={() => setIsAutoPlaying(!isAutoPlaying)}
            className="w-10 h-10 bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-full flex items-center justify-center text-white transition-organic"
            aria-label={isAutoPlaying ? 'Pause slideshow' : 'Play slideshow'}
          >
            <Icon name={isAutoPlaying ? 'Pause' : 'Play'} size={16} />
          </button>
        </div>
      </div>

      {/* Video Modal */}
      <VideoModal
        isOpen={isVideoModalOpen}
        onClose={closeVideoModal}
        videoUrl={demoVideoUrl}
        title="Dandeli Adventure Resort Experience"
      />
    </>
  );
};

export default HeroCarousel;